import { create } from 'zustand';

export interface QuizQuestion {
  id: string;
  type: 'single-choice' | 'true-false' | 'short-answer';
  question: string;
  options?: string[];
  correctAnswer: string | number;
  explanation: string;
  hint?: string;
}

export interface QuizState {
  currentQuestion: number;
  answers: Record<string, string | number>;
  isCompleted: boolean;
  score: number;
  showResults: boolean;
  timeSpent: number;
  startTime: number | null;
}

export interface QuizActions {
  setAnswer: (questionId: string, answer: string | number) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
  submitQuiz: () => void;
  resetQuiz: () => void;
  startQuiz: () => void;
}

const sampleQuestions: QuizQuestion[] = [
  {
    id: 'q1',
    type: 'single-choice',
    question: 'What is the result of 2³ + 4² ?',
    options: ['20', '24', '28', '32'],
    correctAnswer: 1, // Index of correct answer (24)
    explanation: '2³ = 8 and 4² = 16, so 8 + 16 = 24',
    hint: 'Calculate each power separately, then add them together',
  },
  {
    id: 'q2',
    type: 'true-false',
    question: 'The capital of Vietnam is Ho Chi Minh City.',
    options: ['True', 'False'],
    correctAnswer: 1, // False
    explanation: 'The capital of Vietnam is Hanoi, not Ho Chi Minh City.',
    hint: 'Think about the political center of Vietnam',
  },
  {
    id: 'q3',
    type: 'single-choice',
    question: 'Which word is closest in meaning to "abundant"?',
    options: ['Scarce', 'Plentiful', 'Limited', 'Rare'],
    correctAnswer: 1, // Plentiful
    explanation: '"Abundant" means existing in large quantities; plentiful.',
    hint: 'Think about the opposite of scarce',
  },
];

export const useMiniQuizStore = create<QuizState & QuizActions>((set, get) => ({
  // Initial state
  currentQuestion: 0,
  answers: {},
  isCompleted: false,
  score: 0,
  showResults: false,
  timeSpent: 0,
  startTime: null,

  // Actions
  setAnswer: (questionId: string, answer: string | number) => {
    set(state => ({
      answers: { ...state.answers, [questionId]: answer },
    }));
  },

  nextQuestion: () => {
    const { currentQuestion } = get();
    if (currentQuestion < sampleQuestions.length - 1) {
      set({ currentQuestion: currentQuestion + 1 });
    }
  },

  previousQuestion: () => {
    const { currentQuestion } = get();
    if (currentQuestion > 0) {
      set({ currentQuestion: currentQuestion - 1 });
    }
  },

  submitQuiz: () => {
    const { answers, startTime } = get();
    const endTime = Date.now();
    const timeSpent = startTime ? Math.round((endTime - startTime) / 1000) : 0;

    // Calculate score
    let correctAnswers = 0;
    sampleQuestions.forEach(question => {
      if (answers[question.id] === question.correctAnswer) {
        correctAnswers++;
      }
    });

    const score = Math.round((correctAnswers / sampleQuestions.length) * 100);

    set({
      isCompleted: true,
      showResults: true,
      score,
      timeSpent,
    });
  },

  resetQuiz: () => {
    set({
      currentQuestion: 0,
      answers: {},
      isCompleted: false,
      score: 0,
      showResults: false,
      timeSpent: 0,
      startTime: null,
    });
  },

  startQuiz: () => {
    set({
      startTime: Date.now(),
      currentQuestion: 0,
      answers: {},
      isCompleted: false,
      showResults: false,
    });
  },
}));

export { sampleQuestions };
